import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { Chuckle, Bob, OnInk } from "./Characters";
import { PinkButton } from "./Buttons";
import { Reveal } from "./Reveal";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;

const Box = ({ id, checked, onChange, children }) => (
  <label htmlFor={id} className="flex cursor-pointer items-start gap-3 rounded-2xl p-2 font-body text-base leading-snug sm:text-lg">
    <input id={id} data-testid={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="peer sr-only" />
    <span aria-hidden="true" className="check-ui mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg border-2 border-ink bg-white text-ink transition-colors peer-checked:border-pop peer-checked:bg-pop">
      {checked && <Check size={18} strokeWidth={3} />}
    </span>
    <span>{children}</span>
  </label>
);

const Success = ({ email }) => (
  <motion.div data-testid="signup-success" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 18 }}
    className="flex flex-col items-center rounded-[36px] bg-sun px-6 py-12 text-center text-ink" role="status">
    <OnInk className="h-36 w-36"><Bob className="w-[66%]"><Chuckle className="h-auto w-full" /></Bob></OnInk>
    <h3 className="mt-6 font-head text-4xl">You're in!</h3>
    <p className="mt-3 max-w-sm font-body text-lg">We'll be in touch at <span className="font-bold break-all">{email}</span>. Now go make us feel something.</p>
  </motion.div>
);

export const SignUp = ({ intent }) => {
  const [email, setEmail] = useState("");
  const [pack, setPack] = useState(false);
  const [updates, setUpdates] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!intent) return;
    setPack(intent.type === "pack");
    setUpdates(intent.type === "updates");
  }, [intent]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!pack && !updates) { setError("Tick at least one box so we know what to send."); return; }
    if (!API) { setError("Sign-ups are not connected yet. Please try again later."); return; }
    setStatus("loading");
    try {
      await axios.post(`${API}/signups`, { email, wants_pack: pack, wants_updates: updates });
      setStatus("done");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Please check your email address and try again.");
      setStatus("idle");
    }
  };

  return (
    <section id="signup" data-testid="signup-section" className="bg-paper py-20 text-ink sm:py-28">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-10">
        <Reveal>
          <p className="font-body text-base font-semibold text-ink/80">Sign up</p>
          <h2 className="mt-3 font-head text-5xl leading-none tracking-tight sm:text-6xl lg:text-7xl">Ready when you are.</h2>
          <p className="mt-6 max-w-md font-body text-lg leading-relaxed text-ink/85">Get the free preparation pack, follow new films, or both.</p>
        </Reveal>
        <div>
          {status === "done" ? <Success email={email} /> : (
            <form onSubmit={submit} noValidate data-testid="signup-form" className="rounded-[36px] border-2 border-ink bg-white p-6 shadow-[8px_8px_0_#1B1340] sm:p-9">
              <label htmlFor="signup-email" className="font-body text-base font-semibold">Email</label>
              <input id="signup-email" data-testid="signup-email-input" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" className="mt-2 w-full rounded-2xl border-2 border-ink bg-paper px-4 py-3.5 font-body text-lg text-ink placeholder:text-ink/50 focus:border-pop" />
              <div className="mt-6 flex flex-col gap-2">
                <Box id="signup-checkbox-pack" checked={pack} onChange={setPack}>Send me the preparation pack</Box>
                <Box id="signup-checkbox-updates" checked={updates} onChange={setUpdates}>Keep me updated on new films and news until the unveiling</Box>
              </div>
              {error && <p data-testid="signup-error" role="alert" className="mt-4 rounded-xl bg-pop/15 px-4 py-2 font-body text-base font-semibold text-ink">{error}</p>}
              <PinkButton type="submit" data-testid="signup-submit-button" disabled={status === "loading"} className="mt-7 w-full sm:w-auto">
                {status === "loading" && <Loader2 size={18} className="animate-spin" />} Get the pack
              </PinkButton>
              <p className="mt-4 font-body text-sm text-ink/80">We'll only email you about this contest. Unsubscribe any time.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
